import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useActiveService } from 'hooks/useActiveService';
import { Hidden } from '@mui/material';
import SimpleBar from 'simplebar-react';
import { useTranslation } from 'react-i18next';
import Menu from '../menu/Menu';
import UserMenu from '../menu/UserMenu';
import HeaderLinks from './HeaderLinks';
import ContextSelector from '../menu/ContextSelector';
import { sidebarWrapperHeight } from 'utils/constants';
import { Drawer, SidebarRef, StyledImage, StyledLogo, StyledLogoMini } from './SidebarStyle';
import { getApplicationLogo } from 'utils/theme';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { getOidcConfigName } from 'utils/functions';
import logo from 'assets/img/logo.png';
import miniLogo from 'assets/img/miniLogo.png';

function SidebarWrapper({ children, drawerOpen }) {
  const sidebarWrapperRef = useRef();

  return (
    <SidebarRef ref={sidebarWrapperRef} drawerOpen={drawerOpen}>
      <SimpleBar style={{ height: sidebarWrapperHeight, overflowX: drawerOpen ? 'visible' : 'hidden', overflowY: 'hidden' }}>
        {children}
      </SimpleBar>
    </SidebarRef>
  );
}

SidebarWrapper.propTypes = {
  children: PropTypes.array.isRequired,
  drawerOpen: PropTypes.bool.isRequired
};

const Sidebar = ({ closeDrawer, changeLanguage, drawerOpen, drawerMinimized, hasAccess, userInfo }) => {
  const { activeServiceLoaded } = useActiveService();
  const [, i18n] = useTranslation();

  const [logoSrc, setLogoSrc] = useState(null);
  const loadingLogo = !userInfo;
  const isSmallSidebar = !drawerMinimized; //drawerMinimized is not correctly set
  const { accessToken } = useOidcAccessToken(getOidcConfigName());

  const defaultLogo = useMemo(() => (isSmallSidebar ? miniLogo : logo), [isSmallSidebar]);

  useEffect(() => {
    if (!accessToken) return;
    const src = getApplicationLogo(isSmallSidebar, accessToken);
    setLogoSrc(src);
  }, [accessToken, isSmallSidebar]);

  const handleError = useCallback(() => setLogoSrc(defaultLogo), [defaultLogo]);
  var brand = (
    <StyledLogo>
      <StyledLogoMini href='/'>{!loadingLogo && <StyledImage src={logoSrc} alt='logo' onError={handleError} />}</StyledLogoMini>
    </StyledLogo>
  );

  return (
    activeServiceLoaded && (
      <>
        <Hidden mdUp>
          <Drawer
            variant='temporary'
            anchor='right'
            open={drawerOpen}
            onClose={closeDrawer}
            ModalProps={{
              keepMounted: true // Better open performance on mobile.
            }}
            drawerOpen={drawerOpen}
          >
            {userInfo && brand}
            <SidebarWrapper drawerOpen={drawerOpen}>
              {userInfo && (
                <UserMenu changeLanguage={changeLanguage} language={i18n.language} userInfo={userInfo} drawerOpen={drawerOpen} />
              )}
              {hasAccess && <HeaderLinks />}
              <ContextSelector />
              {hasAccess && <Menu drawerOpen={drawerOpen} />}
            </SidebarWrapper>
          </Drawer>
        </Hidden>
        <Hidden mdDown>
          <Drawer anchor='left' variant='permanent' drawerOpen={drawerMinimized}>
            {userInfo && brand}
            <SidebarWrapper drawerOpen={drawerMinimized}>
              {userInfo && (
                <UserMenu changeLanguage={changeLanguage} language={i18n.language} userInfo={userInfo} drawerOpen={drawerMinimized} />
              )}
              {drawerMinimized && <ContextSelector />}
              {hasAccess && <Menu drawerOpen={drawerMinimized} />}
            </SidebarWrapper>
          </Drawer>
        </Hidden>
      </>
    )
  );
};

Sidebar.propTypes = {
  closeDrawer: PropTypes.func.isRequired,
  changeLanguage: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool,
  drawerMinimized: PropTypes.bool,
  hasAccess: PropTypes.bool,
  userInfo: PropTypes.object
};

export default Sidebar;
